import type { DtoolsDisplaySettings, DtoolsPortalPaymentTerm } from "./dtoolsDisplaySettings";
import { normalizeStoredDtoolsVisibilityKeys } from "./dtoolsProposalContent";
import { resolveDtoolsReviewPaymentTerms } from "./dtoolsDisplaySettings";

export type CommercialDraftPayload = {
  paymentTerms: DtoolsPortalPaymentTerm[];
  displaySettings: DtoolsDisplaySettings;
};

/**
 * Payment terms included in draft signatures and parent emits.
 * When payment terms are hidden (showPaymentTerms !== true), stored header terms are ignored
 * so props and emit payloads stay aligned (avoids Revise page infinite update loops).
 */
export function commercialDraftPaymentTermsForSync(
  paymentTerms: DtoolsPortalPaymentTerm[],
  displaySettings: DtoolsDisplaySettings,
  paymentTermsJson?: unknown,
): DtoolsPortalPaymentTerm[] {
  if (displaySettings.showPaymentTerms !== true) return [];
  return resolveDtoolsReviewPaymentTerms(paymentTerms, displaySettings, paymentTermsJson);
}

function normalizePaymentTermsForSignature(terms: DtoolsPortalPaymentTerm[]): DtoolsPortalPaymentTerm[] {
  return terms.map((term) => ({
    stage: term.stage,
    percentage: term.percentage,
    ...(term.fixedAmount != null && term.fixedAmount > 0 ? { fixedAmount: term.fixedAmount } : {}),
  }));
}

/** Stable signature for draft commercial props — avoids reference-only churn in effects. */
export function commercialDraftSignature(
  paymentTerms: DtoolsPortalPaymentTerm[],
  displaySettings: DtoolsDisplaySettings,
): string {
  return JSON.stringify({
    paymentTerms: normalizePaymentTermsForSignature(paymentTerms),
    displaySettings: {
      hideItemPricing: Boolean(displaySettings.hideItemPricing),
      alternativeOptions: Boolean(displaySettings.alternativeOptions),
      hideClarityIntroduction: Boolean(displaySettings.hideClarityIntroduction),
      allowIndividualSystemApproval: Boolean(displaySettings.allowIndividualSystemApproval),
      /** Missing defaults to included — signature must distinguish explicit false. */
      includeExecutiveOverview: displaySettings.includeExecutiveOverview !== false,
      showPaymentTerms: displaySettings.showPaymentTerms === true,
      hiddenOptionKeys: [...(normalizeStoredDtoolsVisibilityKeys(displaySettings.hiddenOptionKeys) ?? [])].sort(),
      hiddenLocationKeys: [...(normalizeStoredDtoolsVisibilityKeys(displaySettings.hiddenLocationKeys) ?? [])].sort(),
      customerExposeInternalOptionKeys: [
        ...(normalizeStoredDtoolsVisibilityKeys(displaySettings.customerExposeInternalOptionKeys) ?? []),
      ].sort(),
    },
  });
}

export function commercialDraftPayloadEquals(a: CommercialDraftPayload, b: CommercialDraftPayload): boolean {
  return commercialDraftSignature(a.paymentTerms, a.displaySettings) === commercialDraftSignature(b.paymentTerms, b.displaySettings);
}

export function displaySettingsSignature(displaySettings: DtoolsDisplaySettings): string {
  return commercialDraftSignature([], displaySettings);
}

export function displaySettingsChanged(
  before: DtoolsDisplaySettings,
  after: DtoolsDisplaySettings,
): boolean {
  return displaySettingsSignature(before) !== displaySettingsSignature(after);
}

export function buildCommercialDraftPayload(input: {
  hideItemPricing: boolean;
  alternativeOptions: boolean;
  showPaymentTerms: boolean;
  hideClarityIntroduction: boolean;
  allowIndividualSystemApproval: boolean;
  hiddenOptionKeys?: string[];
  hiddenLocationKeys?: string[];
  customerExposeInternalOptionKeys?: string[];
  paymentTerms: DtoolsPortalPaymentTerm[];
  paymentValidationValid: boolean;
  parsedTerms: DtoolsPortalPaymentTerm[];
  draftPaymentTerms: DtoolsPortalPaymentTerm[];
}): CommercialDraftPayload {
  const displaySettings: DtoolsDisplaySettings = {
    hideItemPricing: input.hideItemPricing,
    alternativeOptions: input.alternativeOptions,
    showPaymentTerms: input.showPaymentTerms,
    hideClarityIntroduction: input.hideClarityIntroduction,
    allowIndividualSystemApproval: input.allowIndividualSystemApproval,
    ...(input.hiddenOptionKeys !== undefined ? { hiddenOptionKeys: input.hiddenOptionKeys } : {}),
    ...(input.hiddenLocationKeys !== undefined ? { hiddenLocationKeys: input.hiddenLocationKeys } : {}),
    ...(input.customerExposeInternalOptionKeys !== undefined
      ? { customerExposeInternalOptionKeys: input.customerExposeInternalOptionKeys }
      : {}),
  };
  const paymentTerms = commercialDraftPaymentTermsForSync(
    input.showPaymentTerms
      ? input.paymentValidationValid
        ? input.parsedTerms
        : input.draftPaymentTerms
      : [],
    displaySettings,
  );
  return { paymentTerms, displaySettings };
}

/**
 * Returns true when a draft emit should call parent onChange.
 * Prevents Review Assembly infinite update loops from redundant identical payloads.
 */
export function shouldEmitCommercialDraft(
  lastEmittedSignature: string | null,
  payload: CommercialDraftPayload,
): { emit: boolean; signature: string } {
  const signature = commercialDraftSignature(payload.paymentTerms, payload.displaySettings);
  if (lastEmittedSignature === signature) {
    return { emit: false, signature };
  }
  return { emit: true, signature };
}
